import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { Button } from '@shared/ui-react/Button';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Events } from '@shared/Events';
import { isModel } from '@shared/utils/utils';
import { Dropdown } from '@shared/ui-react/Dropdown';

interface IProps {
    modelUuid: string;
    getAttachments: (modelUuid: string) => JQueryPromise<any>;
    addNewAttachment: (modelUuid: string, data: IAttachmentData) => JQueryPromise<any>;
    deleteAttachment: (attachmentId: string, modelUuid: string) => JQueryPromise<any>;
    treeData?: any;
    useModelId?: boolean;
    maxHeight?: number | string;
    disabled?: boolean;
}

const AttachDocumentsAndLinks: React.FC<IProps> = ({ modelUuid, getAttachments, addNewAttachment, deleteAttachment, treeData, useModelId, maxHeight, disabled }) => {
    const [attachedDocuments, setAttachedDocuments] = useState<any[]>([]);
    const [shownDocument, setShownDocument] = useState<boolean>(true);
    const [attachedDocumentName, setAttachedDocumentName] = useState('');
    const [attachedDocumentFileUuid, setAttachedDocumentFileUuid] = useState('');
    const [attachedDocumentFileName, setAttachedDocumentFileName] = useState('');
    const [attachedLinks, setAttachedLinks] = useState<any[]>([]);
    const [shownLink, setShownLink] = useState<boolean>(true);
    const [attachedLinkName, setAttachedLinkName] = useState('');
    const [attachedLinkUrl, setAttachedLinkUrl] = useState('');

    const fetchAttachedDocuments = useCallback(() => {
        if (!!modelUuid) {
            getAttachments(modelUuid).done(response => {
                if (!response.hasOwnProperty('attachments')) {
                    console.error('Missing argument - attachments');
                }
                const documents = [];
                const links = [];
                response.attachments.forEach(att => {
                    if (att.attachedUrl == null) {
                        documents.push(att);
                    } else {
                        links.push(att);
                    }
                });
                setAttachedDocuments(documents);
                setAttachedLinks(links);
            });
        }
    }, [modelUuid]);

    useEffect(fetchAttachedDocuments, [fetchAttachedDocuments]);

    const handleDocumentValueChange = documentUuid => {
        //store.setActiveDocument(parseInt(documentId, 10));
        setAttachedDocumentFileUuid(documentUuid);
        setAttachedDocumentFileName('');
        findTreeDocumentName(treeData.folders, documentUuid);
    };

    const handleSplitDocumentName = (name: string) => {
        const tmp = name.split('.', 2);
        return {
            name: tmp[0],
            extension: tmp[1],
        };
    };

    const findTreeDocumentName = (folders, fileUuid) => {
        for (let folder of folders) {
            if (folder.models && folder.models.length) {
                for (let model of folder.models) {
                    if (model.uuid === fileUuid || (useModelId && model.id === +fileUuid)) {
                        setAttachedDocumentFileName(handleSplitDocumentName(model.name).name);
                    }
                }
            }
            if (!attachedDocumentName && folder.length) {
                folder.forEach(f => {
                    findTreeDocumentName(f, fileUuid);
                });
            } else if (folder.folders && folder.folders.length) {
                findTreeDocumentName(folder.folders, fileUuid);
            }
        }
    };

    const handleAttachNewDocument = () => {
        const postData = useModelId
            ? {
                  name: !!attachedDocumentName ? attachedDocumentName : attachedDocumentFileName,
                  fileId: attachedDocumentFileUuid,
              }
            : {
                  name: !!attachedDocumentName ? attachedDocumentName : attachedDocumentFileName,
                  fileUuid: attachedDocumentFileUuid,
              };

        addNewAttachment(modelUuid, postData).done(() => {
            fetchAttachedDocuments();
            setShownDocument(true);
            setAttachedDocumentName('');
            setAttachedDocumentFileUuid('');
            setAttachedDocumentFileName('');
        });
    };

    const sanitizeName = (value: string): string => {
        let sanitized = value.replace(/<[^>]*>/g, '');
        sanitized = sanitized.replace(/\b(script|onerror|onload|onclick|eval|expression|javascript|vbscript)\b/gi, '');
        sanitized = sanitized.replace(/--|;\s*(drop|insert|update|delete|select|create|alter|exec)\b/gi, '');
        sanitized = sanitized.replace(/[^a-zA-Z0-9\u00C0-\u024F\s.,\-_()[\]]/g, '');
        return sanitized;
    };

    const sanitizeUrl = (value: string): string => {
        const trimmed = value.trim();
        if (/^(javascript|data|vbscript):/i.test(trimmed)) {
            return '';
        }
        return trimmed.replace(/[<>"'`\s]/g, '');
    };

    const handleAttachNewLink = () => {
        const uncompletedUrlRegex = /^(www\.)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=]+$/;

        const postData = {
            name: attachedLinkName,
            attachedUrl: uncompletedUrlRegex.test(attachedLinkUrl) ? `//${attachedLinkUrl}` : attachedLinkUrl,
        };

        addNewAttachment(modelUuid, postData).done(() => {
            fetchAttachedDocuments();
            setShownLink(true);
            setAttachedLinkName('');
            setAttachedLinkUrl('');
        });
    };

    return (
        <div className='model-component-details'>
            <div style={{ overflow: 'auto', maxHeight: maxHeight ?? 250 }}>
                {Object.entries(attachedDocuments).map(([key, value]) => {
                    const node = FileTreeService.findNodeByUuid(treeData, value.documentUuid);
                    const ext = node ? utils.getExtensionFromFilename(node?.name) : null;
                    const isDoc = ext === 'pdf' || ext === 'docx' || ext === 'txt';
                    const isAModel = !isDoc && isModel(node?.name);

                    return (
                        <div key={key} className='flex-row ai-center'>
                            <span style={{ marginRight: 10 }} title={node?.name}>
                                {value.name}
                            </span>

                            {(isDoc || isAModel) && (
                                <Button
                                    onClick={() => (isDoc ? Events.trigger('load-document', node) : Events.trigger('load-model', node.id))}
                                    tooltip={__('js_images.show_on_map')}
                                    icon='iki iki-map-pin'
                                />
                            )}

                            <Dropdown closeOnBodyClick={true} className='ik-button-small ik-button-secondary--subtle'>
                                <i className='iki iki-more' />

                                <ul className='clickable'>
                                    {node && kuura.hasPrivilege('MODEL_PAGE') && (
                                        <li onClick={() => window.open(kuura.getContextPath() + '/models.htm#/' + node.folderId + '/' + node.id)}>
                                            <i className='iki iki-move'></i> {__('js_menu.open_in_files_page')}
                                        </li>
                                    )}

                                    {kuura.hasPrivilege('Download_Model') && (
                                        <li onClick={() => window.open(`/kuura/api/1/file/${useModelId ? value.documentId : `uuid/` + value.documentUuid}/get`)}>
                                            <i className='iki iki-download'> </i>
                                            {__('general.download')}
                                        </li>
                                    )}

                                    {!disabled && (
                                        <li onClick={() => deleteAttachment(value.id, modelUuid).done(fetchAttachedDocuments)}>
                                            <i className='iki iki-trash'> </i>
                                            {__('general.delete')}
                                        </li>
                                    )}
                                </ul>
                            </Dropdown>
                        </div>
                    );
                })}
            </div>
            {shownDocument && !disabled && (
                <button style={{ marginTop: 5 }} onClick={() => setShownDocument(false)}>
                    {__('js_visualization.attach_document')}
                </button>
            )}
            <div>
                {!shownDocument && (
                    <div data-no-drag={true} style={{ display: 'flex', marginTop: 5 }}>
                        <input type='text' placeholder={__('general.name')} value={attachedDocumentName} onChange={e => setAttachedDocumentName(sanitizeName(e.target.value))} />
                        <div style={{ display: 'flex', height: '26px', maxWidth: '220px' }}>
                            <FiletreeSelectReact
                                selectable='file'
                                includeRoot={false}
                                treeData={treeData}
                                hideEmptyFolders={true}
                                canClearValue={true}
                                selectedId={attachedDocumentFileUuid}
                                onChange={handleDocumentValueChange}
                                headerControlsFilterOnly={true}
                                useUuid={!useModelId}
                            />
                        </div>
                        <button style={{ padding: '4px' }} disabled={!attachedDocumentFileUuid} onClick={handleAttachNewDocument}>
                            <i className='iki iki-check' />
                        </button>
                    </div>
                )}
            </div>
            <div>
                {Object.entries(attachedLinks).map(([key, value]) => (
                    <div key={key} className='flex-row ai-center'>
                        <span style={{ marginRight: 10 }}>
                            <a href={value.attachedUrl} target='_blank'>
                                {value.name}
                            </a>
                        </span>

                        {
                            <button onClick={() => deleteAttachment(value.id, modelUuid).done(fetchAttachedDocuments)} style={{ margin: '5px 0px 0px 10px', padding: '3px' }}>
                                <i className='iki iki-trash' />
                            </button>
                        }
                    </div>
                ))}
            </div>
            {shownLink && !disabled && (
                <button style={{ marginTop: 5 }} onClick={() => setShownLink(false)}>
                    {__('js_visualization.attach_link')}
                </button>
            )}
            <div>
                {!shownLink && (
                    <div data-no-drag={true} style={{ display: 'flex', marginTop: 5 }}>
                        <input type='text' placeholder={__('general.name')} value={attachedLinkName} onChange={e => setAttachedLinkName(sanitizeName(e.target.value))} />
                        <input type='text' placeholder='URL' value={attachedLinkUrl} onChange={e => setAttachedLinkUrl(sanitizeUrl(e.target.value))} />
                        <button style={{ padding: '4px' }} disabled={!attachedLinkUrl || !attachedLinkName} onClick={handleAttachNewLink}>
                            <i className='iki iki-check' />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttachDocumentsAndLinks;
